import { createSlice } from "@reduxjs/toolkit";
import { ReducerRegistry } from "@neumetric/blueprint-lib/store";

const DecisionList = [
  { id: "pending", label: "Pending" },
  { id: "acceptable", label: "Acceptable", dotIconName: "APPROVED" },
  { id: "treat", label: "Treat", dotIconName: "LOW" },
  { id: "terminate", label: "Terminate", dotIconName: "MEDIUM" },
  { id: "transfer", label: "Transfer", dotIconName: "MEDIUM" },
  { id: "tolerate", label: "Tolerate", dotIconName: "URGENT" },
  { id: "redundant", label: "Redundant" },
  { id: "inapplicable", label: "Inapplicable" },
];
const SeverityList = [
  { id: "Critical", label: "Critical" },
  { id: "High", label: "High" },
  { id: "Medium", label: "Medium" },
  { id: "Low", label: "Low" },
  { id: "Minimal", label: "Minimal" },
  { id: "None", label: "None" },
];

const StatusList = [
  { id: "pending", label: "Pending" },
  { id: "started", label: "Started" },
  { id: "accepted", label: "Accepted", dotIconName: "APPROVED", isCompleted: true },
  { id: "partial", label: "Partial", dotIconName: "PARTIAL" },
  { id: "treated", label: "Treated", dotIconName: "APPROVED", isCompleted: true },
  { id: "terminated", label: "Terminated", dotIconName: "APPROVED", isCompleted: true },
  { id: "transferred", label: "Transferred", dotIconName: "APPROVED", isCompleted: true },
  { id: "tolerated", label: "Tolerated", dotIconName: "MEDIUM", isCompleted: true },
  { id: "completed", label: "Completed", dotIconName: "APPROVED", isCompleted: true },
  { id: "closed", label: "Closed", dotIconName: "APPROVED", isCompleted: true },
  { id: "redundant", label: "Redundant" },
  { id: "inapplicable", label: "Inapplicable" },
];

const RatingsTypes = [
  { id: "Catastrophic", label: "Catastrophic", dotIconName: "EMERGENCY" },
  { id: "Major", label: "Major", dotIconName: "URGENT" },
  { id: "Moderate", label: "Moderate", dotIconName: "HIGH" },
  { id: "Minor", label: "Minor", dotIconName: "MEDIUM" },
  { id: "Trivial", label: "Trivial", dotIconName: "LOW" },
  { id: "None", label: "None", dotIconName: "COMPLETE" },
  { id: "Critical", label: "Critical", dotIconName: "CRITICAL" },
  { id: "High", label: "High", dotIconName: "HIGH" },
  { id: "Medium", label: "Medium", dotIconName: "MEDIUM" },
  { id: "Low", label: "Low", dotIconName: "LOW" },
  { id: "Minimal", label: "Minimal", dotIconName: "MINIMAL" },
];

const CategoryList = [
  { id: "Human Resources Security", label: "Human Resources Security" },
  {
    id: "Physical & Environmental Security",
    label: "Physical & Environmental Security",
  },
  {
    id: "Communications & Operations Management",
    label: "Communications & Operations Management",
  },
  { id: "Access Control", label: "Access Control" },
  {
    id: "Systems Acquisition, Development & Maintenance",
    label: "Systems Acquisition, Development & Maintenance",
  },
];
const getTime = (x) => {
  const v = x?.created_at || x?.updated_at;
  return v ? new Date(v).getTime() : 0;
};

const sortByRecentFirst = (a, b) => {
  const diff = getTime(b) - getTime(a);
  if (diff !== 0) return diff;
  // tie-breaker so order is stable
  return (b?.id || 0) - (a?.id || 0);
};

const initialState = {
  DecisionList,
  SeverityList,
  StatusList,
  CategoryList,
  RatingsTypes,
  sourcesList: [],
  threatTypesList: [],
  threatsList: [],
  vulnerabilitiesList: [],
  assetsList: [],
  assetTypesList: [],
  ratingsList: [],
  referencesList: [],
  activeSources: [],
  draftSources: [],
  archiveSources: [],
  activeThreatTypes: [],
  draftThreatTypes: [],
  archiveThreatTypes: [],
  activeThreats: [],
  draftThreats: [],
  archiveThreats: [],
  activeVulnerabilities: [],
  draftVulnerabilities: [],
  archiveVulnerabilities: [],
  activeReferences: [],
  draftReferences: [],
  archiveReferences: [],
  currentSource: {},
  currentThreatType: {},
  currentThreat: {},
  currentVulnerability: {},
  currentAsset: {},
  currentAssetType: {},
  currentReference: {},
  risksList: [],
  currentRisk: null,
  importResult: null,
};

const mitigatorSlice = createSlice({
  name: "mitigator",
  initialState: initialState,
  reducers: {
    fetchSources: () => { },
    fetchThreatTypes: () => { },
    fetchThreats: () => { },
    fetchVulnerabilities: () => { },
    fetchAssetList: () => { },
    fetchAssetTypeList: () => { },
    fetchRatings: () => { },
    fetchReferences: () => { },
    createSource: () => { },
    importSource: () => { },
    updateSource: () => { },
    createThreat: () => { },
    importThreat: () => { },
    updateThreat: () => { },
    createThreatType: () => { },
    importThreatType: () => { },
    updateThreatType: () => { },
    createVulnerability: () => { },
    importVulnerability: () => { },
    updateVulnerability: () => { },
    createAsset: () => { },
    importAsset: () => { },
    updateAsset: () => { },
    createAssetType: () => { },
    importAssetType: () => { },
    updateAssetType: () => { },
    createReference: () => { },
    importReference: () => { },
    updateReference: () => { },
    fetchSource: () => { },
    fetchThreatType: () => { },
    fetchThreat: () => { },
    fetchVulnerability: () => { },
    fetchAsset: () => { },
    fetchAssetType: () => { },
    fetchReference: () => { },
    createRisk: () => { },
    fetchRisks: () => { },
    fetchRisk: () => { },
    updateRisk: () => { },
    importRisks: () => { },
    createRiskControl: () => { },
    deleteRiskControl: () => { },
    bulkUpdateThreatStatus: () => { },
    bulkUpdateVulnerabilityStatus: () => { },
    updateRatings: () => { },
    setSourcesList: (state, action) => {
      const list = Array.isArray(action.payload) ? action.payload : [];

      const sorted = list.slice().sort(sortByRecentFirst);

      const active = [];
      const draft = [];
      const archive = [];

      sorted.forEach((x) => {
        const st = (x.status || "draft").toLowerCase();
        if (st === "active") active.push(x);
        else if (st === "archive" || st === "archived") archive.push(x);
        else draft.push(x);
      });

      state.activeSources = active;
      state.draftSources = draft;
      state.archiveSources = archive;
    },
    updateSourceInList: (state, action) => {
      const updated = action.payload;

      state.activeSources = state.activeSources.filter((s) => s.id !== updated.id);
      state.draftSources = state.draftSources.filter((s) => s.id !== updated.id);
      state.archiveSources = state.archiveSources.filter((s) => s.id !== updated.id);

      const newStatus = (updated.status || "draft").toLowerCase();

      let target;
      if (newStatus === "active") target = state.activeSources;
      else if (newStatus === "archive" || newStatus === "archived") target = state.archiveSources;
      else target = state.draftSources;

      target.push(updated);
      target.sort(sortByRecentFirst);
    },
    setImportResult: (state, action) => {
      state.importResult = action.payload;
    },
    clearImportResult: (state) => {
      state.importResult = null;
    },
    setThreatTypesList: (state, action) => {
      const list = Array.isArray(action.payload) ? action.payload : [];

      const sorted = list.slice().sort(sortByRecentFirst);

      const active = [];
      const draft = [];
      const archive = [];

      sorted.forEach((x) => {
        const st = (x.status || "draft").toLowerCase();
        if (st === "active") active.push(x);
        else if (st === "archive" || st === "archived") archive.push(x);
        else draft.push(x);
      });
      state.activeThreatTypes = active || [];
      state.draftThreatTypes = draft || [];
      state.archiveThreatTypes = archive || [];
    },
    updateThreatTypeInList: (state, action) => {
      const updated = action.payload;
      state.activeThreatTypes = state.activeThreatTypes.filter((s) => s.id !== updated.id);
      state.draftThreatTypes = state.draftThreatTypes.filter((s) => s.id !== updated.id);
      state.archiveThreatTypes = state.archiveThreatTypes.filter((s) => s.id !== updated.id);
      const newStatus = (updated.status || "draft").toLowerCase();
      let target;
      if (newStatus === "active") target = state.activeThreatTypes;
      else if (newStatus === "archive" || newStatus === "archived") target = state.archiveThreatTypes;
      else target = state.draftThreatTypes;
      target.push(updated);
      target.sort(sortByRecentFirst);
    },
    setThreatsList: (state, action) => {
      const list = Array.isArray(action.payload) ? action.payload : [];

      const sorted = list.slice().sort(sortByRecentFirst);

      const active = [];
      const draft = [];
      const archive = [];

      sorted.forEach((x) => {
        const st = (x.status || "draft").toLowerCase();
        if (st === "active") active.push(x);
        else if (st === "archive" || st === "archived") archive.push(x);
        else draft.push(x);
      });
      state.activeThreats = active || [];
      state.draftThreats = draft || [];
      state.archiveThreats = archive || [];
    },
    updateThreatInList: (state, action) => {
      const updated = action.payload;
      state.activeThreats = state.activeThreats.filter((s) => s.id !== updated.id);
      state.draftThreats = state.draftThreats.filter((s) => s.id !== updated.id);
      state.archiveThreats = state.archiveThreats.filter((s) => s.id !== updated.id);
      const newStatus = (updated.status || "draft").toLowerCase();
      let target;
      if (newStatus === "active") target = state.activeThreats;
      else if (newStatus === "archive" || newStatus === "archived") target = state.archiveThreats;
      else target = state.draftThreats;
      target.push(updated);
      target.sort(sortByRecentFirst);
    },
    setVulnerabilitiesList: (state, action) => {
      const list = Array.isArray(action.payload) ? action.payload : [];

      const sorted = list.slice().sort(sortByRecentFirst);

      const active = [];
      const draft = [];
      const archive = [];

      sorted.forEach((x) => {
        const st = (x.status || "draft").toLowerCase();
        if (st === "active") active.push(x);
        else if (st === "archive" || st === "archived") archive.push(x);
        else draft.push(x);
      });
      state.activeVulnerabilities = active || [];
      state.draftVulnerabilities = draft || [];
      state.archiveVulnerabilities = archive || [];
    },
    updateVulnerabilityInList: (state, action) => {
      const updated = action.payload;
      state.activeVulnerabilities = state.activeVulnerabilities.filter((s) => s.id !== updated.id);
      state.draftVulnerabilities = state.draftVulnerabilities.filter((s) => s.id !== updated.id);
      state.archiveVulnerabilities = state.archiveVulnerabilities.filter((s) => s.id !== updated.id);
      const newStatus = (updated.status || "draft").toLowerCase();
      let target;
      if (newStatus === "active") target = state.activeVulnerabilities;
      else if (newStatus === "archive" || newStatus === "archived") target = state.archiveVulnerabilities;
      else target = state.draftVulnerabilities;
      target.push(updated);
      target.sort(sortByRecentFirst);
    },
    setAssetsList: (state, action) => {
      state.assetsList = action.payload;
    },
    updateAssetInList: (state, action) => {
      const updated = action.payload;

      state.assetsList = state.assetsList.map((item) => (item.id === updated.id ? { ...item, ...updated } : item));
    },
    setAssetTypesList: (state, action) => {
      state.assetTypesList = action.payload;
    },
    updateAssetTypeInList: (state, action) => {
      const updated = action.payload;

      state.assetTypesList = state.assetTypesList.map((item) =>
        item.id === updated.id ? { ...item, ...updated } : item
      );
    },
    setRatingsList: (state, action) => {
      state.ratingsList = action.payload;
    },
    setReferencesList: (state, action) => {
      const list = Array.isArray(action.payload) ? action.payload : [];

      const sorted = list.slice().sort(sortByRecentFirst);

      const active = [];
      const draft = [];
      const archive = [];

      sorted.forEach((x) => {
        const st = (x.status || "draft").toLowerCase();
        if (st === "active") active.push(x);
        else if (st === "archive" || st === "archived") archive.push(x);
        else draft.push(x);
      });
      state.activeReferences = active || [];
      state.draftReferences = draft || [];
      state.archiveReferences = archive || [];
    },
    updateReferenceInList: (state, action) => {
      const updated = action.payload;
      state.activeReferences = state.activeReferences.filter((s) => s.id !== updated.id);
      state.draftReferences = state.draftReferences.filter((s) => s.id !== updated.id);
      state.archiveReferences = state.archiveReferences.filter((s) => s.id !== updated.id);
      const newStatus = (updated.status || "draft").toLowerCase();
      let target;
      if (newStatus === "active") target = state.activeReferences;
      else if (newStatus === "archive" || newStatus === "archived") target = state.archiveReferences;
      else target = state.draftReferences;
      target.push(updated);
      target.sort(sortByRecentFirst);
    },
    setSource: (state, action) => {
      state.currentSource = action.payload;
    },
    setThreatType: (state, action) => {
      state.currentThreatType = { ...action.payload };
    },
    setThreat: (state, action) => {
      state.currentThreat = { ...action.payload };
    },
    setVulnerability: (state, action) => {
      state.currentVulnerability = { ...action.payload };
    },
    setAsset: (state, action) => {
      state.currentAsset = { ...action.payload };
    },
    setAssetType: (state, action) => {
      state.currentAssetType = { ...action.payload };
    },
    setReference: (state, action) => {
      state.currentReference = { ...action.payload };
    },
    setRisksList: (state, action) => {
      state.risksList = action.payload;
    },
    setCurrentRisk: (state, action) => {
      state.currentRisk = action.payload;
    },
  },
});
export const mitigatorActions = mitigatorSlice.actions;

ReducerRegistry.register("mitigator", mitigatorSlice.reducer);
